import React, { useEffect, useRef, useState } from "react";
import {
  getDeviceExpandInfo,
  getDeviceList,
  getLiveVideoStream,
} from "../utility";
import flvjs from "flv.js";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LiveMapPanel from "./LiveMapPanel";
import EventsSlidePanel from "./EventsSlidePanel";

const GRID_SIZES = [1, 2, 4, 9, 16];

// ── Blueprint channel placeholder ─────────────────────────
const ChannelPlaceholder = ({ index, muted, onMuteToggle }) => (
  <div
    style={{
      position: "relative",
      background: "linear-gradient(160deg,#c8d8f0 0%,#b0c4e8 40%,#d6e4f7 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      minHeight: 0,
    }}
  >
    <svg
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        opacity: 0.3,
      }}
      viewBox="0 0 400 280"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern
          id={`g${index}`}
          width="40"
          height="40"
          patternUnits="userSpaceOnUse"
        >
          <path d="M40 0H0V40" fill="none" stroke="#6a8fc8" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="400" height="280" fill={`url(#g${index})`} />
      <line x1="200" y1="140" x2="0" y2="0" stroke="#8aabdc" strokeWidth="0.8" opacity="0.5" />
      <line x1="200" y1="140" x2="400" y2="0" stroke="#8aabdc" strokeWidth="0.8" opacity="0.5" />
      <line x1="200" y1="140" x2="0" y2="280" stroke="#8aabdc" strokeWidth="0.8" opacity="0.5" />
      <line x1="200" y1="140" x2="400" y2="280" stroke="#8aabdc" strokeWidth="0.8" opacity="0.5" />
      <rect x="60" y="40" width="280" height="200" fill="none" stroke="#8aabdc" strokeWidth="0.8" opacity="0.4" />
    </svg>

    <span
      style={{
        position: "absolute",
        top: 8,
        left: 10,
        fontSize: 11,
        fontWeight: 600,
        color: "rgba(255,255,255,0.9)",
        background: "rgba(0,0,0,0.28)",
        padding: "2px 7px",
        borderRadius: 4,
      }}
    >
      CH {index + 1}
    </span>

    <button
      onClick={() => onMuteToggle(index)}
      title={muted ? "Unmute" : "Mute"}
      style={{
        position: "absolute",
        bottom: 0,
        right: 0,
        background: "rgba(0,0,0,0.6)",
        border: "none",
        cursor: "pointer",
        padding: "6px 10px",
        color: "#fff",
        display: "flex",
        alignItems: "center",
      }}
    >
      {muted ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
        </svg>
      )}
    </button>
  </div>
);

// ── Video Label Bar (matches screenshot design) ────────────
const VideoLabelBar = ({ stream, streamIndex, videoRef, playersRef }) => {
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [bitrate, setBitrate] = useState("--");

  useEffect(() => {
    const id = setInterval(() => {
      const player = playersRef.current[streamIndex];
      if (player && player._statisticsInfo) {
        const kb = (player._statisticsInfo.speed / 1024).toFixed(2);
        setBitrate(`${kb}KB/s`);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [streamIndex, playersRef]);

  const handleMute = () => {
    const vid = videoRef.current;
    if (vid) {
      vid.muted = !vid.muted;
      setMuted(vid.muted);
    }
  };

  const handlePause = () => {
    const player = playersRef.current[streamIndex];
    if (!player) return;
    if (paused) {
      player.play();
    } else {
      player.pause();
    }
    setPaused((p) => !p);
  };

  const handleSnapshot = () => {
    const vid = videoRef.current;
    if (!vid) return;
    const canvas = document.createElement("canvas");
    canvas.width = vid.videoWidth;
    canvas.height = vid.videoHeight;
    canvas.getContext("2d").drawImage(vid, 0, 0);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `snapshot_${stream.deviceName}_CH${stream.channelId}.png`;
    a.click();
  };

  const handleFullscreen = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.requestFullscreen) vid.requestFullscreen();
  };

  const label = `${stream.deviceName}(${stream.deviceId})-CH${stream.channelId}`;

  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 32,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        padding: "0 8px",
        gap: 0,
        zIndex: 10,
      }}
    >
      <span
        style={{
          flex: 1,
          fontSize: 11,
          fontWeight: 500,
          color: "#fff",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          letterSpacing: "0.01em",
        }}
      >
        {label}
      </span>

      <span
        style={{
          fontSize: 11,
          color: "rgba(255,255,255,0.75)",
          marginRight: 10,
          whiteSpace: "nowrap",
        }}
      >
        {bitrate}
      </span>

      <button onClick={handleMute} title={muted ? "Unmute" : "Mute"} style={labelIconBtn}>
        {muted ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM16.5 3v2.06c2.89.86 5 3.54 5 6.94s-2.11 6.08-5 6.94V21c3.96-.91 7-4.49 7-8.96S20.46 3.91 16.5 3z" />
          </svg>
        )}
      </button>

      <button onClick={handlePause} title={paused ? "Play" : "Pause"} style={labelIconBtn}>
        {paused ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
          </svg>
        )}
      </button>

      <button onClick={handleSnapshot} title="Snapshot" style={labelIconBtn}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9.64 7.64c.23-.5.36-1.05.36-1.64 0-2.21-1.79-4-4-4S2 3.79 2 6s1.79 4 4 4c.59 0 1.14-.13 1.64-.36L10 12l-2.36 2.36C7.14 14.13 6.59 14 6 14c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4c0-.59-.13-1.14-.36-1.64L12 14l7 7h3v-1L9.64 7.64zM6 8c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm0 12c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm6-7.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5.5.22.5.5-.22.5-.5.5zM19 3l-6 6 2 2 7-7V3z" />
        </svg>
      </button>

      <button onClick={handleFullscreen} title="Fullscreen" style={labelIconBtn}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
        </svg>
      </button>
    </div>
  );
};

const labelIconBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "rgba(255,255,255,0.85)",
  padding: "0 5px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
};

// ── Icon button base style ────────────────────────────────
const iconBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "#8b93a5",
  width: "28px",
  height: "28px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// Action row: car icon now opens the Events slide-up panel for this
// device; location icon still opens/focuses the map panel exactly as
// before. Each gets its own highlighted state.
const ActionRow = ({
  deviceId,
  onLocationClick,
  selectedMapDevice,
  onVehicleClick,
  selectedEventsDevice,
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-around",
      alignItems: "center",
      padding: "8px 12px",
      borderTop: "1px solid #f0f2f7",
    }}
  >
    <button
      style={{
        ...iconBtn,
        color: selectedEventsDevice === deviceId ? "#082D6B" : "#8b93a5",
        background: selectedEventsDevice === deviceId ? "#e8efff" : "transparent",
        borderRadius: "50%",
      }}
      title="Events"
      // onClick={(e) => {
      //   e.stopPropagation();
      //   onVehicleClick && onVehicleClick(deviceId);
      // }}
    >
      <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18 }} />
    </button>
    <button
      style={{
        ...iconBtn,
        color: selectedMapDevice === deviceId ? "#082D6B" : "#8b93a5",
        background: selectedMapDevice === deviceId ? "#e8efff" : "transparent",
        borderRadius: "50%",
      }}
      title="Location"
      onClick={(e) => {
        e.stopPropagation();
        onLocationClick && onLocationClick(deviceId);
      }}
    >
      <PlaceOutlinedIcon sx={{ fontSize: 18 }} />
    </button>
    <button style={iconBtn} title="Grid">
      <AppsOutlinedIcon sx={{ fontSize: 18 }} />
    </button>
    <button style={iconBtn} title="Snapshot">
      <PhotoCameraOutlinedIcon sx={{ fontSize: 18 }} />
    </button>
    <button style={iconBtn} title="Intercom">
      <LinkOutlinedIcon sx={{ fontSize: 18 }} />
    </button>
  </div>
);

// ── Channel chips ─────────────────────────────────────────
const ChannelChips = ({ channels = [], deviceId, onChannelSelect, activeStreams, isOnline }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 5,
      padding: "5px 12px 6px 36px",
    }}
  >
    {channels.map((channel) => {
      const isActive = activeStreams.some(
        (s) => s && s.deviceId === deviceId && s.channelId === channel.id,
      );
      return (
        <label
          key={channel.id}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 14,
            fontWeight: 500,
            color: isOnline ? "#1a2540" : "#b5b5b5",
            cursor: isOnline ? "pointer" : "not-allowed",
            opacity: isOnline ? 1 : 0.5,
          }}
        >
          <input
            type="checkbox"
            disabled={!isOnline}
            checked={isActive}
            style={{ cursor: isOnline ? "pointer" : "not-allowed" }}
            onChange={(e) => onChannelSelect(deviceId, channel.id, e.target.checked)}
          />
          {channel.name}
        </label>
      );
    })}
  </div>
);

const DeviceCard = ({
  device,
  expandData,
  selected,
  onSelect,
  expanded,
  onToggle,
  onChannelSelect,
  activeStreams,
  onLocationClick,
  selectedMapDevice,
  onVehicleClick,
  selectedEventsDevice,
}) => {
  const isOnline = device.status === "Online";
  return (
    <div style={{ borderBottom: "1px solid #eef0f4" }}>
      <div
        onClick={() => {
          onSelect(device);
          onToggle(device);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 14px",
          cursor: "pointer",
          background: selected ? "#eef2ff" : "transparent",
          transition: "background 0.12s",
        }}
        onMouseEnter={(e) => {
          if (!selected) e.currentTarget.style.background = "#f6f8fc";
        }}
        onMouseLeave={(e) => {
          if (!selected) e.currentTarget.style.background = "transparent";
        }}
      >
        <span
          style={{
            fontSize: 25,
            color: "#082D6B",
            fontWeight: 700,
            width: 16,
            flexShrink: 0,
            userSelect: "none",
            lineHeight: 1,
          }}
        >
          {expanded ? "−" : "+"}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "#1a2540",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {device.name}
          </div>
          <div style={{ fontSize: 13, color: "#9aa0b0", marginTop: 2 }}>
            {device.imei}
          </div>
        </div>

        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "2px 7px",
            borderRadius: 10,
            flexShrink: 0,
            background: isOnline ? "#e8f5e9" : "#f5f5f5",
            color: isOnline ? "#2e7d32" : "#757575",
            border: `1px solid ${isOnline ? "#a5d6a7" : "#e0e0e0"}`,
          }}
        >
          {isOnline ? "● " : "○ "} {device.status}
        </span>
      </div>

      {expanded && (
        <>
          <ChannelChips
            deviceId={device.id}
            channels={expandData?.channelName ? JSON.parse(expandData.channelName) : []}
            onChannelSelect={onChannelSelect}
            activeStreams={activeStreams}
            isOnline={device.status === "Online"}
          />
          <ActionRow
            deviceId={device.id}
            onLocationClick={onLocationClick}
            selectedMapDevice={selectedMapDevice}
            onVehicleClick={onVehicleClick}
            selectedEventsDevice={selectedEventsDevice}
          />
        </>
      )}
    </div>
  );
};

// ── Device Panel ──────────────────────────────────────────
const DevicePanel = ({
  devices,
  deviceExpandData,
  selectedDevice,
  expandedId,
  searchTerm,
  onSelect,
  onToggle,
  onSearch,
  loading,
  onChannelSelect,
  activeStreams,
  onLocationClick,
  selectedMapDevice,
  onVehicleClick,
  selectedEventsDevice,
}) => {
  const filtered = (devices || []).filter(
    (d) =>
      d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.imei?.includes(searchTerm),
  );

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "#fff",
        borderRight: "1px solid #e4e7ef",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "8px 12px", borderBottom: "1px solid #eef0f4", flexShrink: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            background: "#f5f6fa",
            border: "1px solid #e4e7ef",
            borderRadius: 6,
            padding: "5px 9px",
            gap: 6,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search IMEI or Device ID"
            value={searchTerm}
            onChange={onSearch}
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 11,
              color: "#1a2540",
              width: "100%",
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center" }}>Loading Devices...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "#bbb", fontSize: 12 }}>
            No devices found
          </div>
        ) : (
          filtered.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              expandData={deviceExpandData?.[device.id]}
              selected={selectedDevice?.id === device.id}
              onSelect={onSelect}
              expanded={expandedId.includes(device.id)}
              onToggle={onToggle}
              onChannelSelect={onChannelSelect}
              activeStreams={activeStreams}
              selectedMapDevice={selectedMapDevice}
              onLocationClick={onLocationClick}
              onVehicleClick={onVehicleClick}
              selectedEventsDevice={selectedEventsDevice}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ── Video Cell ────────────────────────────────────────────
const VideoCell = ({ stream, streamIndex, videoRefs, playersRef }) => {
  const vidRef = useRef(null);

  useEffect(() => {
    videoRefs.current[streamIndex] = vidRef.current;
  });

  return (
    <div
      style={{
        position: "relative",
        background: "#000",
        overflow: "hidden",
        minHeight: 0,
        width: "100%",
        height: "100%",
      }}
    >
      <video
        ref={vidRef}
        muted
        autoPlay
        playsInline
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <VideoLabelBar
        stream={stream}
        streamIndex={streamIndex}
        videoRef={vidRef}
        playersRef={playersRef}
      />
    </div>
  );
};

// ── Main Dashcam ──────────────────────────────────────────
const Dashcam = () => {
  const [devices, setDevices] = useState([]);
  const [selectedMapDevice, setSelectedMapDevice] = useState(null);
  const [deviceExpandData, setDeviceExpandData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [expandedId, setExpandedId] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [gridSize, setGridSize] = useState(4);
  const [mutedChannels, setMutedChannels] = useState({});
  const [deviceSidebarOpen, setDeviceSidebarOpen] = useState(true);
  const [videoStreams, setVideoStreams] = useState([]);

  const [mapPanelOpen, setMapPanelOpen] = useState(false);
  const [mapFocusDeviceId, setMapFocusDeviceId] = useState(null);

  // Events slide-up panel: which device's events are showing (null = closed).
  const [eventsDeviceId, setEventsDeviceId] = useState(null);
  const eventsDevice = devices.find((d) => d.id === eventsDeviceId) || null;

  const videoRefs = useRef({});
  const playersRef = useRef({});

  const handleMuteToggle = (idx) =>
    setMutedChannels((p) => ({ ...p, [idx]: !p[idx] }));

  const cols = gridSize === 1 ? 1 : gridSize <= 4 ? 2 : gridSize <= 9 ? 3 : 4;
  const rows = Math.ceil(gridSize / cols);

  const destroyStreamAtIndex = (index) => {
    if (playersRef.current[index]) {
      try {
        playersRef.current[index].destroy();
      } catch (_) {}
      delete playersRef.current[index];
    }
    delete videoRefs.current[index];
  };

  const handleChannelSelect = async (deviceId, channelId, checked) => {
    if (!checked) {
      setVideoStreams((prev) => {
        const newStreams = [...prev];
        const index = newStreams.findIndex(
          (v) => v && v.deviceId === deviceId && v.channelId === channelId,
        );
        if (index !== -1) {
          destroyStreamAtIndex(index);
          newStreams[index] = null;
        }
        return newStreams;
      });
      return;
    }

    const alreadyStreaming = videoStreams.some(
      (v) => v && v.deviceId === deviceId && v.channelId === channelId,
    );
    if (alreadyStreaming) return;

    try {
      const response = await getLiveVideoStream({
        deviceId,
        channelId,
        bitstreamType: 1,
        videoChannel: "india-1",
      });

      if (response?.code === 0) {
        const device = devices.find((d) => d.id === deviceId);
        setVideoStreams((prev) => {
          const newStreams = [...prev];
          const emptyIndex = newStreams.findIndex((item) => item === null);
          const streamObj = {
            deviceId,
            channelId,
            url: response.data.videoUrl,
            deviceName: device?.name || deviceId,
          };
          if (emptyIndex !== -1) {
            newStreams[emptyIndex] = streamObj;
          } else {
            newStreams.push(streamObj);
          }
          return newStreams;
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchDeviceList = async () => {
    try {
      setLoading(true);
      const response = await getDeviceList({ subsumption: true });
      if (response?.code === 0) {
        const formattedData = response.data.map((item) => ({
          id: item.id,
          imei: item.id,
          name: item.deviceName,
          status: item?.expand.status === true ? "Online" : "Offline",
          channels: item.camCount,
          customerName: item.customerName,
          deviceType: item.deviceType,
          activeTime: item.activeTime,
          expirationTime: item.expirationTime,
        }));
        setDevices(formattedData);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (device) => {
    const isExpanded = expandedId.includes(device.id);

    if (isExpanded) {
      setExpandedId((prev) => prev.filter((id) => id !== device.id));
      return;
    }

    setExpandedId((prev) => [...prev, device.id]);

    if (!deviceExpandData[device.id]) {
      try {
        const response = await getDeviceExpandInfo(device.imei);
        if (response?.code === 0) {
          setDeviceExpandData((prev) => ({ ...prev, [device.id]: response.data }));
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleLocationClick = (deviceId) => {
    if (selectedMapDevice === deviceId) {
      setSelectedMapDevice(null);
      setMapFocusDeviceId(null);
      return;
    }

    setSelectedMapDevice(deviceId);
    setMapFocusDeviceId(deviceId);
    setMapPanelOpen(true);
  };

  const handleCloseMapPanel = () => {
    setMapPanelOpen(false);
    setMapFocusDeviceId(null);
    setSelectedMapDevice(null);
  };

  // Car icon → toggle the events slide-up panel for this device. Map
  // panel (if open) is untouched — it just shrinks vertically to make
  // room, handled purely in the layout below.
  const handleVehicleClick = (deviceId) => {
    setEventsDeviceId((prev) => (prev === deviceId ? null : deviceId));
  };

  const handleCloseEventsPanel = () => {
    setEventsDeviceId(null);
  };

  useEffect(() => {
    fetchDeviceList();
  }, []);

  useEffect(() => {
    videoStreams.forEach((stream, index) => {
      if (!stream) return;
      const videoElement = videoRefs.current[index];

      if (videoElement && flvjs.isSupported() && !playersRef.current[index]) {
        const player = flvjs.createPlayer({ type: "flv", url: stream.url });

        player.on(flvjs.Events.ERROR, (errorType, errorDetail) => {
          console.error("FLV ERROR:", errorType, errorDetail, "Index:", index);
          try {
            player.destroy();
          } catch (e) {
            console.log(e);
          }
          delete playersRef.current[index];
          setTimeout(() => {
            setVideoStreams((prev) => [...prev]);
          }, 3000);
        });

        player.attachMediaElement(videoElement);
        player.load();
        player.play().catch((err) => console.error("PLAY ERROR:", err));
        playersRef.current[index] = player;
      }
    });
  }, [videoStreams]);

  useEffect(() => {
    return () => {
      Object.values(playersRef.current).forEach((p) => {
        try {
          p.destroy();
        } catch (_) {}
      });
    };
  }, []);

  const filteredDevices = devices.filter(
    (device) =>
      device.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.imei?.includes(searchTerm),
  );

  const eventsPanelOpen = !!eventsDeviceId;

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "row" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Top Controls */}
        <div
          style={{
            height: 46,
            background: "#fff",
            borderBottom: "1px solid #e4e7ef",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 14px",
            flexShrink: 0,
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1a2540" }}>Device</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", gap: 3 }}>
              {GRID_SIZES.map((size) => (
                <button
                  key={size}
                  onClick={() => setGridSize(size)}
                  style={{
                    width: 26,
                    height: 26,
                    border: "1px solid",
                    borderColor: gridSize === size ? "#082D6B" : "#d8dde8",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontSize: 10,
                    fontWeight: 700,
                    background: gridSize === size ? "#082D6B" : "#fff",
                    color: gridSize === size ? "#fff" : "#666",
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Upper row: device panel + video grid + map panel.
              Shrinks to half-height (via flex-basis below) whenever the
              events panel is open, so events get the bottom half. */}
          <div
            style={{
              flex: eventsPanelOpen ? "0 0 60%" : 1,
              minHeight: 0,
              display: "flex",
              overflow: "hidden",
              transition: "flex-basis 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {/* Device Panel */}
            <div
              style={{
                width: deviceSidebarOpen ? (mapPanelOpen ? 220 : 320) : 0,
                minWidth: deviceSidebarOpen ? (mapPanelOpen ? 220 : 320) : 0,
                overflow: "hidden",
                transition: "width 0.2s ease, min-width 0.2s ease",
                flexShrink: 0,
              }}
            >
              <DevicePanel
                devices={filteredDevices}
                deviceExpandData={deviceExpandData}
                selectedDevice={selectedDevice}
                expandedId={expandedId}
                searchTerm={searchTerm}
                onSelect={setSelectedDevice}
                onToggle={handleToggle}
                onSearch={(e) => setSearchTerm(e.target.value)}
                loading={loading}
                onChannelSelect={handleChannelSelect}
                activeStreams={videoStreams}
                onLocationClick={handleLocationClick}
                selectedMapDevice={selectedMapDevice}
                onVehicleClick={handleVehicleClick}
                selectedEventsDevice={eventsDeviceId}
              />
            </div>

            {/* Collapse Tab */}
            <div style={{ position: "relative", width: 18, flexShrink: 0 }}>
              <button
                onClick={() => setDeviceSidebarOpen((o) => !o)}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  transform: "translateY(-50%)",
                  zIndex: 999,
                  width: 18,
                  height: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#fff",
                  border: "1px solid #e4e7ef",
                  borderLeft: "none",
                  borderRadius: "0 6px 6px 0",
                  cursor: "pointer",
                  color: "#666",
                  boxShadow: "2px 0 6px rgba(0,0,0,0.08)",
                  padding: 0,
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}>
                  {deviceSidebarOpen ? "◀" : "▶"}
                </span>
              </button>
            </div>

            {/* Video Grid — shrinks (doesn't disappear) when map panel opens */}
            <div
              style={{
                flex: mapPanelOpen ? "0 0 40%" : 1,
                minWidth: 0,
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, 1fr)`,
                gap: 2,
                background: "#1e2128",
                padding: 2,
                overflow: "hidden",
                alignItems: "stretch",
                justifyItems: "stretch",
                transition: "flex-basis 0.2s ease",
              }}
            >
              {[...Array(gridSize)].map((_, i) => {
                const stream = videoStreams[i];
                if (stream) {
                  return (
                    <VideoCell
                      key={`${stream.deviceId}-${stream.channelId}`}
                      stream={stream}
                      streamIndex={i}
                      videoRefs={videoRefs}
                      playersRef={playersRef}
                    />
                  );
                }
                return (
                  <ChannelPlaceholder
                    key={i}
                    index={i}
                    muted={!!mutedChannels[i]}
                    onMuteToggle={handleMuteToggle}
                  />
                );
              })}
            </div>

            {/* Live Map Panel — shows on the right when opened via the
                location icon. Video grid above shrinks to make room. */}
            {mapPanelOpen && (
              <div
                style={{
                  flex: "1 1 60%",
                  minWidth: 0,
                  borderLeft: "1px solid #e4e7ef",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
               <LiveMapPanel
  focusDeviceId={mapFocusDeviceId}
  onClose={handleCloseMapPanel}
  eventsPanelOpen={eventsPanelOpen}
/>
              </div>
            )}
          </div>

          {/* Events slide-up panel — bottom half. Animates height in/out
              smoothly; mounted only while a device's events are toggled
              on, so the API call (inside EventsSlidePanel) starts fresh
              each time it opens. */}
          <div
            style={{
              flex: eventsPanelOpen ? "0 0 40%" : "0 0 0%",
              minHeight: 0,
              overflow: "hidden",
              borderTop: eventsPanelOpen ? "1px solid #e4e7ef" : "none",
              boxShadow: eventsPanelOpen ? "0 -6px 18px rgba(8,45,107,0.08)" : "none",
              transition:
                "flex-basis 0.28s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.28s ease",
            }}
          >
            {/* {eventsPanelOpen && (
              <EventsSlidePanel
                deviceId={eventsDeviceId}
                deviceName={eventsDevice?.name}
                onClose={handleCloseEventsPanel}
              />
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashcam;